import { MenuItem, SxProps, TextField, Theme } from "@mui/material";
import dayjs from "dayjs";
import { ChangeEventHandler, useEffect, useState } from "react";
import DateRange from "./DateRange";
import { CampaignReportTimeframe } from "../../Enums/CampaignTypes";

export default function DateRangePicker({
  id,
  startDate,
  endDate,
  handleSetStartDate,
  handleSetEndDate,
  customStartDate,
  customEndDate,
  customTimeframeOption,
  customTimeframeLabel,
  customHandleChangeTimeframe,
  dropdownSx,
  dateRangeSx,
}: {
  id?: string;
  startDate: Date;
  endDate: Date;
  handleSetStartDate: (date: Date | null) => void;
  handleSetEndDate: (date: Date | null) => void;
  customStartDate?: Date;
  customEndDate?: Date;
  customTimeframeOption?: CampaignReportTimeframe;
  customTimeframeLabel?: string;
  customHandleChangeTimeframe?: (e: CampaignReportTimeframe) => void;
  dropdownSx?: SxProps<Theme>;
  dateRangeSx?: SxProps<Theme>;
}) {
  const [timeframeOption, setTimeframeOption] = useState(
    customStartDate && customEndDate ? CampaignReportTimeframe.Custom : CampaignReportTimeframe.Last30Days,
  );
  const workingOption = customTimeframeOption ?? timeframeOption;

  const { startDate: timeframeStart, endDate: timeframeEnd } = getTimeframeDates(
    workingOption,
    customStartDate ?? startDate,
    customEndDate ?? endDate,
  );
  const timeframeDisplay =
    customTimeframeLabel ?? `${timeframeStart.toLocaleDateString()} - ${timeframeEnd.toLocaleDateString()}`;
  const showCustomInputs = workingOption === CampaignReportTimeframe.Custom;

  const handleChangeTimeframe: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    setTimeframeOption(event.target.value as CampaignReportTimeframe);
  };

  useEffect(() => {
    handleSetStartDate(dayjs(timeframeStart).toDate());
    handleSetEndDate(dayjs(timeframeEnd).toDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingOption]);

  return (
    <>
      <TextField
        data-test={id ? id : "timeframe"}
        sx={{ width: 1, mb: showCustomInputs ? 3 : 0, ...dropdownSx }}
        value={workingOption}
        onChange={
          customHandleChangeTimeframe
            ? (e) => customHandleChangeTimeframe(e.target.value as CampaignReportTimeframe)
            : handleChangeTimeframe
        }
        select
        label={timeframeDisplay}
      >
        {Object.values(CampaignReportTimeframe).map((timeframe) => (
          <MenuItem key={timeframe} value={timeframe}>
            {timeframe}
          </MenuItem>
        ))}
      </TextField>
      {showCustomInputs && (
        <DateRange
          startDate={timeframeStart}
          endDate={timeframeEnd}
          startDateOnChange={(d) => handleSetStartDate(dayjs(d).toDate())}
          endDateOnChange={(d) => handleSetEndDate(dayjs(d).toDate())}
          sx={{
            flexDirection: { xs: "row", md: "column", lg: "row" },
            ...dateRangeSx,
          }}
        />
      )}
    </>
  );
}

function getLast30DaysDates() {
  const today = dayjs().startOf("day");
  const startDate = today.subtract(30, "day");
  return {
    startDate: startDate.toDate(),
    endDate: today.toDate(),
  };
}

function getLast90DaysDates() {
  const today = dayjs().startOf("day");
  const startDate = today.subtract(90, "day");
  return {
    startDate: startDate.toDate(),
    endDate: today.toDate(),
  };
}

function getLastYearDates() {
  const today = dayjs().startOf("day");
  const startDate = today.subtract(1, "year");
  return {
    startDate: startDate.toDate(),
    endDate: today.toDate(),
  };
}

export function getTimeframeDates(timeframe: CampaignReportTimeframe, startDate?: Date, endDate?: Date) {
  switch (timeframe) {
    case CampaignReportTimeframe.Last30Days:
      return getLast30DaysDates();
    case CampaignReportTimeframe.Last90Days:
      return getLast90DaysDates();
    case CampaignReportTimeframe.LastYear:
      return getLastYearDates();
    case CampaignReportTimeframe.Custom:
      if (startDate && endDate) {
        return { startDate: dayjs(startDate).toDate(), endDate: dayjs(endDate).toDate() };
      }
      return getLast30DaysDates();
    default:
      return getLast30DaysDates();
  }
}
